import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { User, Mail, Briefcase, Calendar, Clock, Edit, Key, Trash2, ArrowLeft, CheckCircle, Wifi, Monitor, Activity, Search } from 'lucide-react';
import Button from '@shared/Button';
import Card from '@shared/Card';
import ResponsiveTable from '@shared/ResponsiveTable'; // Updated import
import UserFormModal from '@/components/users/UserFormModal';
import ConfirmationModal from '@shared/ConfirmationModal';
import api from '@/utils/api';
import toast from 'react-hot-toast';
import { format, formatDistanceToNow } from 'date-fns';
import EmptyState from '@shared/EmptyState';
import MarkdownIt from 'markdown-it';

const md = new MarkdownIt();

const ExpandableDocuments: React.FC<{ names: string[] }> = ({ names }) => {
  const [expanded, setExpanded] = useState(false);
  if (!names || names.length === 0) return <span className="text-gray-400 text-sm">—</span>;
  const needsExpand = names.length > 2 || names.some(n => n.length > 25);
  return (
    <div className="w-80 md:w-48 cursor-pointer break-words" onClick={() => setExpanded(e => !e)}>
      <ul className={`text-sm space-y-0.5 ${!expanded ? 'line-clamp-5' : ''}`}>
        {names.map((name, i) => <li key={i}>{name}</li>)}
      </ul>
      {needsExpand && (
        <span className="text-xs text-blue-500 mt-1 block">
          {expanded ? 'Show less' : 'Show more'}
        </span>
      )}
    </div>
  );
};

const ExpandableResponse: React.FC<{ response: string }> = ({ response }) => {
  const [expanded, setExpanded] = useState(false);
  const html = md.render(response);
  return (
    <div className="w-80 md:w-64 cursor-pointer break-words" onClick={() => setExpanded(e => !e)}>
      <div
        className={`markdown-body ${!expanded ? 'line-clamp-5' : ''}`}
        style={{ whiteSpace: 'normal' }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {response.length > 200 && (
        <span className="text-xs text-blue-500 mt-1 block">
          {expanded ? 'Show less' : 'Show more'}
        </span>
      )}
    </div>
  );
};

interface QueryAnalytics {
  _id: string;
  query: string;
  response: string;
  status: string;
  responseTime: number;
  timestamp: string;
  documentNames: string[];
  userAgent: string;
}

interface ActivityLog {
  _id: string;
  action: string;
  details: any;
  timestamp: string;
  ipAddress: string;
}

interface Session {
  _id: string;
  deviceInfo?: string;
  ipAddress?: string;
  isActive: boolean;
  createdAt: string;
  lastActiveAt: string;
}

interface UserAnalytics {
  recentQueries?: QueryAnalytics[];
  activityLogs?: ActivityLog[];
  activeSessions?: Session[];
  querySummary?: {
    totalQueries: number;
    avgResponseTime: number;
    mostQueriedDocument?: string;
    lastQueryTime?: string;
  };
}

interface UserData {
  _id: string;
  name?: string;
  email?: string;
  role?: 'admin' | 'user' | 'viewer';
  status?: string;
  department?: string;
  createdAt?: string;
  lastLogin?: string;
  permissions?: string[];
  [key: string]: any;
}

const UserDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showResetPasswordConfirmation, setShowResetPasswordConfirmation] = useState(false);
  const [activeTab, setActiveTab] = useState('recentQueries'); // 'recentQueries', 'activityLog', 'sessions'

  // New state for tab-specific data
  const [userAnalytics, setUserAnalytics] = useState<UserAnalytics>({});
  const [userAnalyticsLoading, setUserAnalyticsLoading] = useState(false);
  const [userAnalyticsError, setUserAnalyticsError] = useState<string | null>(null);

  const fetchUserAnalytics = useCallback(async () => {
    setUserAnalyticsLoading(true);
    setUserAnalyticsError(null);

    try {
      const analyticsData = await api.getUserAnalytics(id || '');
      setUserAnalytics(analyticsData);
     } catch (err: any) {
      setUserAnalyticsError('Failed to fetch userAnalytics.');
      toast.error('Failed to load userAnalytics.');
      console.error('Error fetching userAnalytics:', err);
    } finally {
      setUserAnalyticsLoading(false);
    }
  }, [id]);

  const fetchUser = useCallback(async () => {
    setLoading(true);
    try {
      const userData = await api.getUser(id || '');
      setUser(userData);
      await fetchUserAnalytics();
    } catch (err: any) {
      setError('Failed to fetch user details.');
      toast.error('Failed to load user details.');
      console.error('Error fetching user:', err);
    } finally {
      setLoading(false);
    }
  }, [id, fetchUserAnalytics]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const handleUserUpdate = async () => {
    setShowEditModal(false);
    await fetchUser();
  };

  const handleDeleteUser = async () => {
    try {
      await api.deleteUser(id || '');
      toast.success('User deleted successfully.');
      navigate('/users');
    } catch (err: any) {
      toast.error('Failed to delete user.');
      console.error('Error deleting user:', err);
    } finally {
      setShowDeleteConfirmation(false);
    }
  };

  const handleResetPassword = async () => {
    try {
      const response = await api.resetUserPassword(id || '');
      toast.success(`Password reset successfully. Temporary password: ${response.temporaryPassword}`);
    } catch (err: any) {
      toast.error('Failed to reset password.');
      console.error('Error resetting password:', err);
    } finally {
      setShowResetPasswordConfirmation(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6 animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/4"></div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 space-y-4">
            <div className="h-48 bg-gray-200 rounded"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
          </div>
          <div className="lg:col-span-2 space-y-4">
            <div className="h-64 bg-gray-200 rounded"></div>
            <div className="h-48 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="p-6 text-center">
        <EmptyState
          icon={User}
          title="User Not Found"
          description={error || "The user you are looking for does not exist or an error occurred."}
          action={{ label: 'Back to Users', onClick: () => navigate('/users') }}
        />
      </div>
    );
  }

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-purple-100 text-purple-800';
      case 'user':
        return 'bg-blue-100 text-blue-800';
      case 'viewer':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Columns for Recent Queries
  const recentQueriesColumns = [
    { key: 'query', header: 'Query', render: (query: string) => <div className="w-80 md:w-30  break-words line-clamp-5">{query}</div> },
    { key: 'response', header: 'Response', render: (response: string) => <ExpandableResponse response={response} /> },
    { key: 'status', header: 'Status', render: (status: string) => <div className="max-w-xs text-center truncate">{status[0].toUpperCase() + status.slice(1)}</div> },
    { key: 'responseTime', header: 'Response Time', render: (responseTime: number) => <div className="max-w-xs text-center truncate">{responseTime < 1000 ? `${Math.round(responseTime)}ms`: `${(responseTime / 1000).toFixed(1)}s`}</div> },
    { key: 'timestamp', header: 'Query Time', render: (timestamp: string) => formatDistanceToNow(new Date(timestamp) , { addSuffix: true }) },
    { key: 'documentNames', header: 'Documents Used', render: (documentNames: string[]) => <ExpandableDocuments names={documentNames} /> },
    { key: 'userAgent', header: 'Query Channel', removeSmall: true },
  ];

  // Columns for Activity Log
  const activityLogColumns = [
    { key: 'action', header: 'Action', render:(action: string) => (action.replace(/_/g, ' ').replace(/\b\w/g, (char: string) => char.toUpperCase())) },
    { key: 'details', header: 'Details', render: (details: any) => (<div className="max-w-xs whitespace-pre-wrap break-words">
          {typeof details === 'object' && details !== null
            ? JSON.stringify(details, null, '\t')
                .replace(/^(\s*)"([^"]+)":/gm, (_match, indent, key) => {
                    let formattedKey = key.replace(/([a-z])([A-Z])/g, '$1 $2');
                    formattedKey = formattedKey.replace(/\b\w/g, (char: string) => char.toUpperCase());
                    return `${indent}${formattedKey}:`;
                })
                .replace(/[{}]/g, '')
                .replace(/\"/g, '')
                .replace(/,\s*$/gm, '')
            : details}
        </div>
    )},
    { key: 'timestamp', header: 'Timestamp', render: (timestamp: string) => formatDistanceToNow(new Date(timestamp), { addSuffix: true }) },
    { key: 'ipAddress', header: 'IP Address', removeSmall: true },
  ];

  // Columns for Recent Sessions
  const sessionsColumns = [
    { key: 'deviceInfo', header: 'Device Information', render: (deviceInfo: string | undefined) => <div className="flex items-center gap-2 whitespace-pre-wrap break-words"><Monitor className="w-5 h-5" />{deviceInfo || 'Unknown Device'}</div> },
    { key: 'ipAddress', header: 'IP Address', render: (ipAddress: string | undefined) => <div className="flex items-center gap-2 whitespace-pre-wrap break-words"><Wifi className="w-4 h-4" /> {ipAddress || 'N/A'}</div> },
    { key: 'isActive', header: 'Session Status', render: (isActive: boolean) => isActive === true ? 'Active' : 'Inactive' },
    { key: 'createdAt', header: 'Creation Date', render: (createdAt: string) => format(new Date(createdAt), 'MMM dd, yyyy HH:mm'), removeSmall: true },
    { key: 'lastActiveAt', header: 'Last Active', render: (lastActiveAt: string) => format(new Date(lastActiveAt), 'MMM dd, yyyy HH:mm') },
    { key: 'action', header: 'Action', render: () => (
      <Button variant="danger" size="sm" onClick={() => toast.error('Revoke session not implemented yet.')}>Revoke</Button>
    ), removeSmall: true },
  ];


  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between mb-6">
        <Button variant="ghost" onClick={() => navigate('/users')} className="flex items-center gap-2">
          <ArrowLeft className="w-5 h-5" /> Back to Users
        </Button>
        <div className="flex items-center gap-3">
          <Button variant="secondary" onClick={() => setShowEditModal(true)} className="flex items-center gap-2">
            <Edit className="w-5 h-5" /> Edit User
          </Button>
          <Button variant="warning" onClick={() => setShowResetPasswordConfirmation(true)} className="flex items-center gap-2">
            <Key className="w-5 h-5" /> Reset Password
          </Button>
        </div>
      </div>

      {/* User Header */}
      <Card className="p-6 flex flex-col md:flex-row items-center gap-6">
        <div className="w-24 h-24 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 text-4xl font-bold">
          {user.name ? user.name.charAt(0).toUpperCase() : <User className="w-12 h-12" />}
        </div>
        <div className="text-center md:text-left">
          <h1 className="text-3xl font-bold text-gray-900">{user.name}</h1>
          <p className="text-lg text-gray-600 flex items-center justify-center md:justify-start gap-2 mt-1">
            <Mail className="w-5 h-5" /> {user.email}
          </p>
          <div className="flex items-center justify-center md:justify-start gap-3 mt-3">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getRoleBadgeClass(user.role || 'user')}`}>
              {user.role}
            </span>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeClass(user.status || 'active')}`}>
              {user.status}
            </span>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Account Information */}
        <Card className="p-6 lg:col-span-1">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Account Information</h2>
          <div className="space-y-3 text-gray-700">
            <p className="flex items-center gap-3"><User className="w-5 h-5 text-gray-500" /> <strong>User ID:</strong> {user._id}</p>
            <p className="flex items-center gap-3"><Briefcase className="w-5 h-5 text-gray-500" /> <strong>Department:</strong> {user.department || 'N/A'}</p>
            <p className="flex items-center gap-3"><Calendar className="w-5 h-5 text-gray-500" /> <strong>Created At:</strong> {user.createdAt ? format(new Date(user.createdAt), 'PPP') : 'N/A'}</p>
            <p className="flex items-center gap-3"><Clock className="w-5 h-5 text-gray-500" /> <strong>Last Login:</strong> {user.lastLogin ? formatDistanceToNow(new Date(user.lastLogin), { addSuffix: true }) : 'Never'}</p>
            <p className="flex items-center gap-3"><Search className="w-5 h-5 text-gray-500" /> <strong>Total Queries:</strong> {userAnalytics?.querySummary?.totalQueries || 0}</p>
          </div>
        </Card>

        {/* Permissions & Activity Summary */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Permissions</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-gray-700">
              {(user.permissions && user.permissions.length > 0) ? (
                user.permissions.map((permission: string, index: number) => (
                  <p key={index} className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" /> {permission.replace(/_/g, ' ').charAt(0).toUpperCase() + permission.replace(/_/g, ' ').slice(1)}
                  </p>
                ))
              ) : (
                <p className="text-gray-500">No specific permissions assigned.</p>
              )}
            </div>
          </Card>

          <Card className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Activity Summary</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-gray-700">
              <div className="flex flex-col items-center text-center">
                <Search className="w-8 h-8 text-primary-500 mb-2" />
                <p className="text-sm text-gray-500">Queries This Month</p>
                <p className="text-xl font-semibold">{userAnalytics?.querySummary?.totalQueries || 0}</p>
              </div>
              <div className="flex flex-col items-center text-center">
                <Clock className="w-8 h-8 text-primary-500 mb-2" />
                <p className="text-sm text-gray-500">Avg Response Time</p>
                <p className="text-xl font-semibold">{(userAnalytics?.querySummary?.avgResponseTime || 0) < 1000 ? `${Math.round(userAnalytics?.querySummary?.avgResponseTime || 0)}ms`: `${((userAnalytics?.querySummary?.avgResponseTime || 0) / 1000).toFixed(1)}s`}</p>
              </div>
              <div className="flex flex-col items-center text-center">
                <User className="w-8 h-8 text-primary-500 mb-2" />
                <p className="text-sm text-gray-500">Most Queried Document</p>
                <p className="text-xl font-semibold">{userAnalytics?.querySummary?.mostQueriedDocument || 'Nothing Queried'}</p>
              </div>
              <div className="flex flex-col items-center text-center">
                <Calendar className="w-8 h-8 text-primary-500 mb-2" />
                <p className="text-sm text-gray-500">Last Query Time</p>
                <p className="text-xl font-semibold">{userAnalytics?.querySummary?.lastQueryTime ? formatDistanceToNow(new Date(userAnalytics.querySummary.lastQueryTime), { addSuffix: true }) : 'Never'}</p>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Recent Activity Tabs */}
      <Card className="p-6">
        <div className="border-b border-gray-200 mb-4">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('recentQueries')}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${activeTab === 'recentQueries' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
            >
              Recent Queries
            </button>
            <button
              onClick={() => setActiveTab('activityLog')}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${activeTab === 'activityLog' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
            >
              Activity Log
            </button>
            <button
              onClick={() => setActiveTab('sessions')}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${activeTab === 'sessions' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
            >
              Sessions
            </button>
          </nav>
        </div>

        <div>
          {activeTab === 'recentQueries' && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Last 10 Queries</h3>
              {userAnalyticsLoading ? (
                <div className="text-center py-8">Loading recent queries...</div>
              ) : userAnalyticsError ? (
                <div className="text-center py-8 text-red-600">{userAnalyticsError}</div>
              ) : (userAnalytics?.recentQueries && userAnalytics.recentQueries.length > 0) ? (
                <ResponsiveTable columns={recentQueriesColumns} data={userAnalytics.recentQueries} keyField="_id" />
              ) : (
                <EmptyState
                  icon={Search}
                  title="No Recent Queries"
                  description="This user has not performed any queries yet."
                />
              )}
            </div>
          )}

          {activeTab === 'activityLog' && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Activity Log</h3>
              {userAnalyticsLoading ? (
                <div className="text-center py-8">Loading activity log...</div>
              ) : userAnalyticsError ? (
                <div className="text-center py-8 text-red-600">{userAnalyticsError}</div>
              ) : (userAnalytics?.activityLogs && userAnalytics.activityLogs.length > 0) ? (
                <ResponsiveTable columns={activityLogColumns} data={userAnalytics.activityLogs} keyField="_id" />
              ) : (
                <EmptyState
                  icon={Activity}
                  title="No Activity Log"
                  description="This user has no recorded activity yet."
                />
              )}
            </div>
          )}

          {activeTab === 'sessions' && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Sessions</h3>
              {userAnalyticsLoading ? (
                <div className="text-center py-8">Loading sessions...</div>
              ) : userAnalyticsError ? (
                <div className="text-center py-8 text-red-600">{userAnalyticsError}</div>
              ) : (userAnalytics?.activeSessions && userAnalytics.activeSessions.length > 0) ? (
                <ResponsiveTable columns={sessionsColumns} data={userAnalytics.activeSessions} keyField="_id" />
              ) : (
                <EmptyState
                  icon={Monitor}
                  title="No Recent Sessions"
                  description="This user has no recent login sessions."
                />
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Danger Zone */}
      <Card className="p-6 border-l-4 border-red-500 bg-red-50">
        <h2 className="text-xl font-semibold text-red-800 mb-4">Danger Zone</h2>
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <p className="font-medium text-red-700">Delete User Account</p>
              <p className="text-sm text-red-600">Permanently remove this user and all their associated data.</p>
            </div>
            <Button variant="danger" onClick={() => setShowDeleteConfirmation(true)}>
              <Trash2 className="w-4 h-4 mr-2" /> Delete Account
            </Button>
          </div>
        </div>
      </Card>

      <UserFormModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSave={handleUserUpdate}
        user={user as any}
      />

      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={handleDeleteUser}
        title="Delete User Account?"
        message={`Are you sure you want to delete ${user.name}'s account? This action cannot be undone.`}
        confirmText="Delete Account"
        confirmButtonVariant="danger"
      />

      <ConfirmationModal
        isOpen={showResetPasswordConfirmation}
        onClose={() => setShowResetPasswordConfirmation(false)}
        onConfirm={handleResetPassword}
        title={`Reset Password for ${user.name}?`}
        message="A temporary password will be generated and displayed. Please provide it to the user securely."
        confirmText="Reset Password"
        confirmButtonVariant="warning"
      />
    </div>
  );
};

export default UserDetailsPage;
